import React from 'react';
import { Link } from 'react-router-dom';
import { SkewButton, Card, GradientDivider } from '../components/UIComponents';
import { Award, Users, Heart, Star, Instagram, ChevronRight } from 'lucide-react';

const POWER_COLLECTIVE_LOGO = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/d8pfhyek_BE9F4541-49A7-4850-BDE2-9F4B1EAB3BEE.png";
const MAIN_LOGO = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/ad5dhro0_Untitled%20design.png";

const coaches = [
  {
    name: "Queen Jean",
    title: "Miss GOALgeous",
    certifications: [
      "Certified Group Fitness Instructor",
      "Certified Xtreme Burn Instructor",
      "Certified Xtreme Step Instructor"
    ],
    bio: "Known as the Queen of Burn Combos. Jean leads high-energy sessions that blend strength, cardio, and core into powerful, innovative workouts. Her classes push members past limits while helping them build discipline, endurance, and real results.",
    accent: "pink",
    icon: "👑"
  },
  {
    name: "Tilena Watts",
    title: "2000 Watts",
    certifications: [
      "Certified Xtreme Burn Instructor",
      "Certified Xtreme Step Instructor"
    ],
    bio: "Known for her signature power circuits that combine strength, cardio, and core. Her workouts are intense, innovative, and always evolving. Tilena brings structure, creativity, and serious energy to every class.",
    accent: "lime",
    icon: "⚡"
  },
  {
    name: "Coach Deuces",
    title: "Club Deuces",
    certifications: [
      "Certified in Xtreme Burn",
      "Certified in Xtreme Step",
      "Certified in Xtreme HD"
    ],
    bio: "Known for her vibe and beginner-friendly teaching style. Coach Deuces is loved for her music, her pacing, and her ability to make everyone feel comfortable while still burning major calories. Her classes are welcoming, fun, and effective.",
    accent: "pink",
    icon: "✌🏾"
  },
  {
    name: "Kita",
    title: "Owner of Lift Fitness",
    certifications: [
      "Certified NASA Instructor",
      "Certified Group Fitness Instructor"
    ],
    bio: "Known for her innovative workouts and powerful spirit. Kita leads dynamic sessions that include strength training, cardio, and core. Her classes are structured, motivating, and designed to build strength inside and out.",
    accent: "lime",
    icon: "💪🏾"
  }
];

export const InstructorsPage = () => {
  return (
    <div data-testid="instructors-page" className="bg-brand-black pt-20">
      {/* Hero Section */}
      <section data-testid="instructors-hero" className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-black via-brand-dark to-brand-black" />
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-0 w-96 h-96 bg-brand-pink/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-brand-lime/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          {/* Logo */}
          <img 
            src={POWER_COLLECTIVE_LOGO} 
            alt="The Power Collective" 
            className="h-48 md:h-64 mx-auto mb-10"
          />
          
          {/* Title */}
          <h1 className="font-oswald text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mb-6">
            MEET THE <span className="text-brand-pink">POWER TEAM</span>
          </h1>
          
          <div className="w-48 h-1 bg-gradient-to-r from-brand-lime via-brand-pink to-brand-lime mx-auto mb-10" />
          
          {/* Subtitle */}
          <div className="max-w-2xl mx-auto">
            <p className="font-montserrat text-xl md:text-2xl text-white leading-relaxed">
              Four women. One mission. <span className="text-brand-lime font-semibold">Strength.</span> <span className="text-brand-pink font-semibold">Accountability.</span> <span className="text-brand-lime font-semibold">Transformation.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Coaches Grid Section */}
      <section data-testid="coaches-grid" className="py-20 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {coaches.map((coach, index) => (
              <div 
                key={coach.name}
                data-testid={`coach-card-${index}`}
                className={`bg-brand-black border-2 ${coach.accent === 'pink' ? 'border-brand-pink/30 hover:border-brand-pink' : 'border-brand-lime/30 hover:border-brand-lime'} p-8 transition-all duration-300 group`}
              >
                {/* Photo Placeholder */}
                <div className="flex flex-col md:flex-row gap-6 mb-6">
                  <div className={`w-32 h-32 flex-shrink-0 rounded-full ${coach.accent === 'pink' ? 'bg-brand-pink/10 border-2 border-brand-pink/30' : 'bg-brand-lime/10 border-2 border-brand-lime/30'} flex items-center justify-center`}>
                    <span className="text-5xl">{coach.icon}</span>
                  </div>
                  
                  <div className="flex-1">
                    {/* Name and Title */}
                    <h3 className={`font-oswald text-2xl md:text-3xl uppercase tracking-tight mb-1 ${coach.accent === 'pink' ? 'text-brand-pink' : 'text-brand-lime'}`}>
                      {coach.name}
                    </h3>
                    <p className="font-oswald text-lg uppercase tracking-wider text-white mb-4">
                      {coach.title}
                    </p>
                    
                    {/* Certifications */}
                    <div className="space-y-1 mb-4">
                      {coach.certifications.map((cert, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Award size={14} className={coach.accent === 'pink' ? 'text-brand-pink' : 'text-brand-lime'} />
                          <span className="font-montserrat text-sm text-brand-gray">{cert}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Bio */}
                <p className="font-montserrat text-brand-gray leading-relaxed mb-6">
                  {coach.bio}
                </p>
                
                {/* Personal Logo Placeholder */}
                <div className={`border border-dashed ${coach.accent === 'pink' ? 'border-brand-pink/30' : 'border-brand-lime/30'} p-4 text-center`}>
                  <p className="font-oswald text-sm uppercase tracking-wider text-brand-gray">
                    Personal Logo Coming Soon
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sisterhood Section */}
      <section data-testid="sisterhood-section" className="py-20 bg-brand-black relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-pink/5 via-transparent to-brand-lime/5" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-brand-pink/10 flex items-center justify-center">
            <Users size={40} className="text-brand-pink" />
          </div>
          
          <h2 className="font-oswald text-3xl md:text-5xl uppercase text-white mb-6">
            A <span className="text-brand-pink">Sisterhood</span> of <span className="text-brand-lime">Strength</span>
          </h2>
          
          <p className="font-montserrat text-lg text-brand-gray leading-relaxed mb-8 max-w-2xl mx-auto">
            Our coaches aren't just instructors—they're leaders, mentors, and part of a powerful sisterhood dedicated to helping women transform their bodies, minds, and lives. Together, we build strength, confidence, and community.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <div className="px-6 py-3 bg-brand-pink/10 border border-brand-pink/30">
              <span className="font-oswald text-brand-pink uppercase tracking-wider">Certified</span>
            </div>
            <div className="px-6 py-3 bg-brand-lime/10 border border-brand-lime/30">
              <span className="font-oswald text-brand-lime uppercase tracking-wider">Experienced</span>
            </div>
            <div className="px-6 py-3 bg-brand-pink/10 border border-brand-pink/30">
              <span className="font-oswald text-brand-pink uppercase tracking-wider">Dedicated</span>
            </div>
            <div className="px-6 py-3 bg-brand-lime/10 border border-brand-lime/30">
              <span className="font-oswald text-brand-lime uppercase tracking-wider">Empowering</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="instructors-cta" className="py-24 bg-brand-dark relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-brand-lime/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-brand-pink/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <img 
            src={MAIN_LOGO} 
            alt="Power Up Your Fitness" 
            className="h-24 mx-auto mb-8"
          />
          
          <h2 className="font-oswald text-3xl md:text-5xl font-bold uppercase text-white mb-6">
            Train With The <span className="text-brand-lime">Best</span>
          </h2>
          
          <p className="font-montserrat text-lg text-brand-gray mb-8">
            Ready to experience the power of our coaches? Book a class and feel the difference.
          </p>
          
          <SkewButton href="https://viagro.com" testId="instructors-book-cta">
            Book a Class
          </SkewButton>
        </div>
      </section>

      {/* Bottom Tagline */}
      <section className="py-12 bg-brand-black border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-oswald text-2xl md:text-3xl uppercase tracking-wider text-white">
            Strong women. Certified leaders. <span className="text-brand-pink">Real results.</span> 💚🩷
          </p>
        </div>
      </section>
    </div>
  );
};
